import React, { useEffect, useState } from 'react';
import { Divider, Tag, Alert } from 'antd';
import { Drawer } from '@prio365/prio365-react-library';
import { useTranslation } from 'react-i18next';
import { apiHelpGetReleaseContent } from '../api';
import i18n from '../../../i18n';
import { openZendeskDrawer } from '../actions/drawer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch } from 'react-redux';
import { ContentDto } from '../../../models/Prio365Features';
import { PrioSpinner } from '@prio365/prio365-react-library';

interface Props {
  visible: boolean;
  onClose: () => void;
  selectedVersion: string;
}

const ReleaseNotesDrawer: React.FC<Props> = ({
  visible,
  onClose,
  selectedVersion,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const closeDrawer = () => dispatch(openZendeskDrawer(false));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [groupedContent, setGroupedContent] = useState<{
    [module: string]: ContentDto[];
  }>({});

  const startUserFlow = (userFlowUrl: string) => {
    closeDrawer();
    // Extract the actual userflow ID from the URL if needed
    const userFlowId = userFlowUrl.includes('/')
      ? userFlowUrl.split('/').pop()
      : userFlowUrl;
    setTimeout(() => window.open(`?userflow=${userFlowId}`, '_self'), 500);
  };

  useEffect(() => {
    if (!selectedVersion || !visible) return;

    const fetchReleaseContent = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data, result } =
          await apiHelpGetReleaseContent(selectedVersion);

        if (result.status >= 200 && result.status < 300 && data) {
          // Partition content into Web and non-Web
          const webItems = data.filter((c) => c.platformType === 'Web');

          // Group only Web content by module
          const grouped = webItems.reduce(
            (acc, content) => {
              const module = content.module || 'General';
              if (!acc[module]) {
                acc[module] = [];
              }
              acc[module].push(content);
              return acc;
            },
            {} as { [module: string]: ContentDto[] }
          );

          setGroupedContent(grouped);
        } else {
          setError(
            t('zendesk:error.loadingReleaseContent') ||
              'Failed to load release content'
          );
        }
      } catch (err) {
        console.error('Error fetching release content:', err);
        setError(
          t('zendesk:error.loadingReleaseContent') ||
            'Failed to load release content'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchReleaseContent();
  }, [selectedVersion, visible, t]);

  const getContentDescription = (content: ContentDto): string => {
    if (i18n.language.includes('de')) {
      return (
        content.descriptionGerman ||
        content.descriptionEnglish ||
        content.generatedContent ||
        ''
      );
    }
    return (
      content.descriptionEnglish ||
      content.descriptionGerman ||
      content.generatedContent ||
      ''
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '50px',
          }}
        >
          <PrioSpinner size="default" />
          <p>{t('zendesk:loading')}</p>
        </div>
      );
    }

    if (error) {
      return (
        <Alert
          message={t('zendesk:error.title') || 'Error'}
          description={error}
          type="error"
          showIcon
          style={{ margin: '20px 0' }}
        />
      );
    }

    if (Object.keys(groupedContent).length === 0) {
      return (
        <Alert
          message={t('zendesk:noContent') || 'No content available'}
          description={
            t('zendesk:noContentDescription') ||
            'No content found for this release version.'
          }
          type="info"
          showIcon
          style={{ margin: '20px 0' }}
        />
      );
    }

    return (
      <div>
        {Object.keys(groupedContent).map((module) => (
          <div key={module}>
            <h2>
              {module === 'General' ? (
                t('zendesk:general')
              ) : (
                <>
                  {t('zendesk:module')}: {module}
                </>
              )}
            </h2>

            {groupedContent[module].map((content) => (
              <div key={content.contentId}>
                {content.title && <h3>{content.title}</h3>}

                {/* Action Tags */}
                <div style={{ marginBottom: '8px' }}>
                  {content.screencast?.screencastUrl && (
                    <Tag
                      color="blue"
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        window.open(
                          content.screencast!.screencastUrl,
                          '_blank'
                        );
                      }}
                    >
                      <FontAwesomeIcon icon={['fal', 'video']} />{' '}
                      {t('zendesk:screencast') || 'Screencast'}
                    </Tag>
                  )}

                  {!content.screencast?.screencastUrl &&
                    content.releaseScreencastUrlWithTimeStamp && (
                      <Tag
                        color="purple"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          window.open(
                            content.releaseScreencastUrlWithTimeStamp,
                            '_blank'
                          );
                        }}
                      >
                        <FontAwesomeIcon icon={['fal', 'video']} />{' '}
                        {t('zendesk:releaseScreencast') || 'Release Video'}
                      </Tag>
                    )}

                  {content.userFlow?.userFlowUrl && (
                    <Tag
                      color="yellow"
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        startUserFlow(content.userFlow!.userFlowUrl!);
                      }}
                    >
                      <FontAwesomeIcon icon={['fal', 'play']} />{' '}
                      {t('zendesk:userFlow') || 'User Flow'}
                    </Tag>
                  )}
                </div>

                {/* Info Tags */}
                <div style={{ marginBottom: '12px' }}>
                  <Tag color="green">Feature</Tag>
                  <Tag color="orange">{selectedVersion}</Tag>
                  {content.subModule && (
                    <Tag color="cyan">{content.subModule}</Tag>
                  )}
                  {content.platformType && (
                    <Tag color="geekblue">{content.platformType}</Tag>
                  )}
                </div>

                {/* Content Description */}
                <div
                  dangerouslySetInnerHTML={{
                    __html: getContentDescription(content),
                  }}
                  style={{ marginBottom: '16px' }}
                />

                <Divider />
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Drawer
      title={`${t('zendesk:version')} ${selectedVersion}`}
      onClose={onClose}
      visible={visible}
    >
      {renderContent()}
    </Drawer>
  );
};

export default ReleaseNotesDrawer;
